import { StatusCodes } from 'http-status-codes';
import type { ContentfulPageResponseModel } from '~~/server/api/contentful/index.get';
import { isContentfulPageRedirectResponse } from '~/utils/contentful/isContentfulPageRedirectResponse';

export interface ContentfulPagePayloadErrorLike {
  statusCode?: number;
  statusMessage?: string;
  message?: string;
}

export interface MissingContentfulPageErrorResult {
  statusCode: number;
  statusMessage: string;
  message: string;
  cause?: unknown;
  fatal: true;
}

/**
 * Derives the Nuxt error payload for missing page responses.
 * Preserves an upstream fetch error (e.g. 403) and only falls back to 404
 * when no redirect and no upstream payload error is available.
 */
export function buildMissingContentfulPageError(
  pagePayloadError: ContentfulPagePayloadErrorLike | null,
  rawContentfulPayload: ContentfulPageResponseModel | null | undefined
): MissingContentfulPageErrorResult | null {
  if (pagePayloadError) {
    return {
      statusCode:
        pagePayloadError.statusCode ?? StatusCodes.INTERNAL_SERVER_ERROR,
      statusMessage: pagePayloadError.statusMessage ?? 'Page request failed',
      message: pagePayloadError.message ?? 'Page request failed',
      cause: pagePayloadError,
      fatal: true
    };
  }

  if (
    rawContentfulPayload &&
    isContentfulPageRedirectResponse(rawContentfulPayload)
  ) {
    return null;
  }

  return {
    statusCode: StatusCodes.NOT_FOUND,
    statusMessage: 'Page not found',
    message: 'Page not found',
    fatal: true
  };
}
